import { TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { differenceBy, get, isEmpty, map } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueries, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'

import GenericInput, { FormComponent } from '@/components/GenericForm/GenericInput'
import { Person } from '@/components/Icons'
import { Role } from '@/components/RolesContainer'
import Button from '@/components/TailwindUI/Button'
import ComboBox, { ComboBoxOption } from '@/components/TailwindUI/ComboBox'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import useFeatures from '@/components/useFeatures'
import { USER_ATTRIBUTES, UserData } from '@/components/User'
import { getUserBySpaceAndId } from '@/components/User/basicQueries'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import ConfirmDialogWithDataSources from '@/pages/SpaceAppBuilder/components/ConfirmDialogWithDataSources'
import ConfirmDialogWithCheckBox from '@/pages/SpaceAppBuilder/components/OwnerConfirmModalWithCheckBox'
import { AppInstanceDataSource, WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'
import fetch from '@/utilities/fetch'

import messages from './messages'

interface ShareWithUsersProps extends FormComponent<string[]> {
  value: string[]
  onSelect: (userIds: string[]) => void
  className?: string
  descriptor?: string
  shouldDisplayLabel?: boolean
  appQueryResult?: any
}
interface SharedUsersDataState {
  sharedWithUsers: MappedSharedWithUser[]
  isLoading: boolean
}
interface MappedSharedWithUser {
  id: string
  name: string
  icon?: any
  email: string
}

const ShareWithUsers = ({
  formatProps: { name, label = 'App Owners', required, error },
  value,
  disabled,
  descriptor = 'app',
  onSelect,
  shouldDisplayLabel = true,
  appQueryResult
}: ShareWithUsersProps) => {
  const intl = useIntl()
  const queryClient = useQueryClient()

  const features = useFeatures()
  const agentsFlagOn = !!features[WORKSHOP_AGENTS]

  const [sharedUsersData, setSharedUsersData] = useState<SharedUsersDataState>({
    sharedWithUsers: [],
    isLoading: false
  })
  const [searchTerm, setSearchTerm] = useState<string>()
  const sharedWithUsersRef = useRef<string[]>()

  const { spaceId } = useParams<{ spaceId: string }>()

  const appDataSources = appQueryResult?.data?.dataSources
  const hasDataSources = appDataSources && appDataSources.length > 0

  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourcesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    appQueryResult?.data.dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key,
        instanceId: ds.id
      }),
      enabled: !!appQueryResult?.app?.dataSources
    })) || []
  )

  const dataSources = dataSourcesQuery.reduce((dataSourcesInstances: WorkgridDatasource[], dataSource) => {
    if (dataSource.data) {
      dataSourcesInstances.push(dataSource.data)
    }
    return dataSourcesInstances
  }, [])

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isAddingOwnerConfirmationModalOpen, setIsAddingOwnerConfirmationModalOpen] = useState(false)
  const [currentUserToRemove, setCurrentUserToRemove] = useState<string>('')
  const [addingOwnerID, setAddingOwnerID] = useState<string | undefined>()

  const openConfirmationModal = (userId: string) => {
    setIsConfirmationModalOpen(true)
    setCurrentUserToRemove(userId)
  }

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false)
    setCurrentUserToRemove('')
  }

  const onRemoveMyself = () => {
    removeSharedUser(currentUserToRemove)
    closeConfirmationModal()
  }

  const currentUser = useAppSelector(getCurrentUser)
  const { id } = currentUser

  const filterUserQueryResults = (users: UserData[]) => {
    const usersWithAppOwnerRole = users.filter(user => {
      return user.roles.spaces[spaceId].some((role: { name: Role }) => role.name === Role.WORKSHOP_APP_OWNER)
    })
    const usersNotAlreadySharedWith = differenceBy(usersWithAppOwnerRole, value, 'id')
    return usersNotAlreadySharedWith
  }

  const searchResults = useQuery({
    queryKey: [`/organizations/:organizationId/spaces/:spaceId/users/search/basic`, searchTerm, spaceId],
    queryFn: async () => {
      const response = await fetch(`/organizations/:organizationId/spaces/:spaceId/users/search/basic`, {
        method: 'POST',
        body: {
          attributes: USER_ATTRIBUTES,
          count: 10,
          startIndex: 1,
          spaceId,
          filter: USER_ATTRIBUTES.map(attribute => `${attribute} co ${JSON.stringify(searchTerm)}`).join(' or ')
        }
      })
      return filterUserQueryResults(get(response, 'resources', []))
    },
    enabled: !isEmpty(searchTerm)
  })

  const fetchSharedUsers = async () => {
    setSharedUsersData({ sharedWithUsers: [], isLoading: true })
    const sharedUsers: MappedSharedWithUser[] = []
    try {
      if (value && !isEmpty(value)) {
        const promises = []

        for (const id of value) {
          promises.push(
            fetchUser(id).then(sharedUser => {
              sharedUsers.push({
                name: sharedUser.displayName || sharedUser.name,
                email: sharedUser.email,
                id: sharedUser.id
              })
            })
          )
        }

        await Promise.allSettled(promises)
      }
      setSharedUsersData({ sharedWithUsers: sharedUsers, isLoading: false })
    } catch {
      setSharedUsersData({ sharedWithUsers: [], isLoading: false })
    }
  }

  useEffect(() => {
    if (!sharedWithUsersRef.current || sharedWithUsersRef.current !== value) {
      fetchSharedUsers()
      sharedWithUsersRef.current = value
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleSelectUser = (userId: string | undefined) => {
    if (userId === undefined) return
    const updatedSharedWith = [...value]
    onSelect(updatedSharedWith.includes(userId) ? updatedSharedWith : [...updatedSharedWith, userId])
    setSearchTerm('')
    setIsAddingOwnerConfirmationModalOpen(false)
    setAddingOwnerID(undefined)
  }

  const handleAddingOwner = (userId: string | undefined) => {
    if (userId === undefined) return
    setAddingOwnerID(userId)
    if (sharedWithUsersRef.current && !sharedWithUsersRef.current.includes(userId) && hasDataSources) {
      setIsAddingOwnerConfirmationModalOpen(true)
    } else {
      handleSelectUser(userId)
    }
  }

  const removeSharedUser = (userId: string) => {
    onSelect(value?.filter((id: string) => id !== userId))
  }

  const fetchUser = async (userId: string) => {
    const existingUser = sharedUsersData.sharedWithUsers.find(user => user.id === userId)

    if (existingUser) {
      return existingUser
    }

    const user: any = await queryClient.fetchQuery(getUserBySpaceAndId({ userId }))
    return user.data
  }

  return (
    <GenericInput
      name={name}
      label={shouldDisplayLabel ? label : undefined}
      required={required}
      error={error}
      icon={shouldDisplayLabel ? <UserGroupIcon /> : undefined}
      details={intl.formatMessage(
        disabled
          ? messages.readOnlyDescription
          : descriptor === 'data source instance'
            ? messages.datasourceDescription
            : agentsFlagOn && descriptor === 'agent'
              ? messages.agentDescription
              : messages.description,
        { descriptor }
      )}
      className={'mt-3'}
    >
      <ConfirmDialogWithCheckBox
        size="sm"
        isOpen={isAddingOwnerConfirmationModalOpen}
        onConfirm={() => handleSelectUser(addingOwnerID)}
        onCancel={() => setIsAddingOwnerConfirmationModalOpen(false)}
        descriptor={descriptor}
        variant="warning"
        cancelText={'Cancel'}
        confirmText={'Add as owners'}
        appDataSources={dataSources}
        currentUserId={id}
      />

      {isConfirmationModalOpen && (
        <ConfirmDialogWithDataSources
          size="sm"
          isOpen={isConfirmationModalOpen}
          onConfirm={onRemoveMyself}
          onCancel={closeConfirmationModal}
          descriptor={descriptor}
          variant="error"
          cancelText={'Keep access'}
          confirmText={'Remove myself'}
          appDataSources={dataSources}
          currentUserId={id}
        />
      )}

      <div className={disabled ? '' : 'flex-wrap'}>
        {!disabled && (
          <div className="flex-column mb-6 mt-1 mr-4 min-w-[300px]">
            <ComboBox
              className="max-w-[500px]"
              name={name}
              isAsync={true}
              isLoading={false}
              placeholder={intl.formatMessage(messages.placeholder)}
              options={
                searchResults.data?.map(searchResult => ({
                  label: searchResult.displayName,
                  displayText: searchResult.email,
                  icon: <Person />,
                  value: searchResult.id
                })) || []
              }
              value={searchTerm}
              onSelect={(option: ComboBoxOption) => {
                descriptor === 'agent' || descriptor === 'app'
                  ? handleAddingOwner(option?.value)
                  : handleSelectUser(option?.value)
              }}
              onChange={setSearchTerm}
              disabled={disabled}
              isAbsolute={false}
            />
          </div>
        )}
        <div className="mt-1.5 min-w-[18rem]">
          {sharedUsersData.isLoading ? (
            <div className="row mt-2 items-center">
              <LoadingSpinner className="mr-2 h-6 w-6" />
              <div className="text-sm text-zinc-500">Loading...</div>
            </div>
          ) : (
            map(sharedUsersData.sharedWithUsers, (user, index) => (
              <div key={index} className="mt-2 flex flex-wrap items-center justify-between">
                <div>
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap">{user.name}</p>
                  <p className="mt-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[#8e99a3]">
                    {user.email}
                  </p>
                </div>
                {sharedUsersData.sharedWithUsers.length !== 1 && !disabled && (
                  <Button
                    className=""
                    color="transparent"
                    onClick={() => (id === user.id ? openConfirmationModal(user.id) : removeSharedUser(user.id))}
                  >
                    <TrashIcon className="h-[18px] w-[18px]" />
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </GenericInput>
  )
}
ShareWithUsers.displayName = 'ShareWithUsers'
export default ShareWithUsers
